/**
 * SOLID Review
 * S — Strong. sole job: implement IAdapterApolloSession's session/identity operations against the real
 *     backend via Apollo's imperative client API. Strong for the one method it has (createPlayer
 *     — Phase 2 Group D, plan/plan_frontend_makeover.md, IdentityModal.tsx's real guest-identity
 *     operation). initializeSession/getLocalUser/getAdConfig/getLeaderboard were deleted as
 *     housekeeping (plan/plan_frontend_makeover.md) — fabricated/idealized placeholders with
 *     zero known callers anywhere in the tree, confirmed after Phase 2 completed.
 * O — Good. A new session operation is a new method here plus a new IAdapterApolloSession signature —
 *     mechanical, no existing method touched.
 * L — N/A — no inheritance hierarchy; implements IAdapterApolloSession directly.
 * I — Good. Implements the same interface every real session operation needs — no longer silently narrower
 *     than the interface promises now that its four fabricated placeholders
 *     (initializeSession/getLocalUser/getAdConfig/getLeaderboard) are gone.
 * D — Strong. Takes ApolloClient as a constructor-injected dependency (same pattern as
 *     ApolloRoomAdapter.ts/ApolloGameAdapter.ts) rather than importing a singleton — testable without a
 *     real network.
 */
import { gql } from '@apollo/client';
import type { Result } from '../../shared/Result';
import type { IEntityCreatePlayer } from '../../application/ports/apolloShapes';
import { ok, fail } from '../../shared/Result';
import type { IAdapterApolloSession } from '../../application/ports/IAdapterApolloSession';
import type { IPortGraphQLDomainClient } from '../contract/ports';

// ---------------------------------------------------------------------------
// GraphQL operations (Phase 2 Group D). Moved here from IdentityModal.tsx's inline definition.
// ---------------------------------------------------------------------------

const CREATE_PLAYER_MUTATION = gql`
  mutation ApolloSessionAdapter_CreatePlayer($displayName: String!) {
    createPlayer(displayName: $displayName) {
      id
      displayName
      matchCredits
      lives
    }
  }
`;

export class ApolloSessionAdapter implements IAdapterApolloSession {
  // The executor arrives from this domain's socket on ComponentFrontendInfrastructure, not as
  // the raw ApolloClient. That socket is the single path from "this app can reach the domain"
  // to "this adapter executes" — disconnect it and this adapter degrades to the Null client
  // instead of quietly using a module singleton (§28.72).
  constructor(private readonly client: IPortGraphQLDomainClient) {}

  async createPlayer(displayName: string): Promise<Result<IEntityCreatePlayer>> {
    try {
      const { data, error } = await this.client.mutate<{ createPlayer: IEntityCreatePlayer | null }>({
        mutation: CREATE_PLAYER_MUTATION,
        variables: { displayName },
      });
      if (error) {
        return fail(new Error(`ApolloSessionAdapter.createPlayer returned a GraphQL error: ${error.message}`));
      }
      if (!data?.createPlayer) {
        return fail(new Error('ApolloSessionAdapter.createPlayer returned no data'));
      }
      return ok(data.createPlayer);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error creating player';
      return fail(new Error(`ApolloSessionAdapter.createPlayer failed: ${message}`));
    }
  }
}
